import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Box,
  Typography,
  useTheme,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Info as InfoIcon
} from '@mui/icons-material';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animated components with delay props
const AnimatedBox = styled(Box)<{ delay?: number; isVisible: boolean }>`
  animation: ${({ isVisible }) => isVisible ? fadeInUp : 'none'} 0.8s ease-out forwards;
  animation-delay: ${({ delay }) => delay || 0}s;
  opacity: 0;
`;

// Feature data structure
interface Feature {
  feature: string;
  description: string;
}

const featuresData: Feature[] = [
  {
    feature: "Max allowed contracts",
    description: "Set maximum contract limits per trade"
  },
  {
    feature: "Daily trade limit",
    description: "Limit number of trades per day"
  },
  {
    feature: "Consecutive loss protection",
    description: "Auto-lock after consecutive losses"
  },
  {
    feature: "Daily loss threshold",
    description: "Stop trading after daily loss limit"
  },
  {
    feature: "Profit target locking",
    description: "Auto-lock when profit target reached"
  },
  {
    feature: "??????",
    description: "Exciting new feature under development"
  },
  {
    feature: "??????",
    description: "Innovative capability coming soon"
  },
  {
    feature: "??????...",
    description: "Cutting-edge functionality in development"
  }
];

const Features: React.FC = () => {
  const theme = useTheme();
  const featuresRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const currentRef = featuresRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated]);

  return (
    <Box 
      sx={{ 
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 }
      }}
      id="features"
      ref={featuresRef}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <AnimatedBox isVisible={isVisible} delay={0.2}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: 'var(--font-primary)'
              }}
            >
              RiskMaster Features
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', fontFamily: 'var(--font-secondary)' }}>
              Discover the complete suite of risk management tools designed for serious traders.
            </Typography>
          </Box>
        </AnimatedBox>

        {/* Features Card */}
        <AnimatedBox isVisible={isVisible} delay={0.4}>
          <Card 
            sx={{ 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h5" color="primary" sx={{ fontFamily: 'var(--font-primary)' }}>
                RiskMaster Features
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ minHeight: 40, mb: 3, fontFamily: 'var(--font-secondary)' }}>
                Core risk management tools available now, with exciting new features in development.
              </Typography>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ mb: 2, fontFamily: 'var(--font-primary)' }}>
                Available & Upcoming Features:
              </Typography>
              <List dense>
                {featuresData.map((item, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary={item.feature} 
                    />
                    <Tooltip title={item.description} arrow placement="left">
                      <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontFamily: 'var(--font-primary)' }}>
                  Want to shape the future of RiskMaster?
                </Typography>
                <Button variant="contained" size="large" fullWidth>
                  Sign Up for Beta Testing
                </Button>
              </Box>
            </CardContent>
          </Card>
        </AnimatedBox>
      </Box>
    </Box>
  );
};

export default Features;
